import React, { useState } from 'react';

import { useAppDispatch } from '../hooks';

import { EmployeeActions } from '../actions/employee/employee.actions';

import type { Employee } from '../interfaces/employee/employee.interface';

import Modal from './components/Modal';

// =============================================================================
//  Create employee modal (v3)
//
//  Backend-required fields:    first_name, last_name, email, password
//                              (password must satisfy User#password_regex:
//                              8+ chars, 1 uppercase, 1 number, 1 special)
//  v3-relevant fields:         erole, team, office, qualifications,
//                              mobile_number, start_date
//
//  We auto-generate a strong random password since the v3 flow doesn't want
//  the admin choosing one — the new employee uses "Forgot password" the first
//  time they log in.  Onboarding admin (ID number, address, leave-balance,
//  employment docs) stays in the legacy form at /human-resources/employees/add.
// =============================================================================

/**
 * Generate a password that satisfies User#password_regex on the backend:
 *   /\A(?=.*\d)(?=.*[A-Z])(?=.*\W)[^ ]{8,}\z/
 * i.e. ≥8 chars, ≥1 uppercase, ≥1 digit, ≥1 non-word character, no spaces.
 */
const generateTempPassword = (): string => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const digit = '23456789';
  const symbol = '!@#$%^&*()_+-=[]{};:,.<>?';
  const all = upper + lower + digit + symbol;
  const pick = (set: string) => set.charAt(Math.floor(Math.random() * set.length));
  // 16 chars total, guaranteed at least one of each required class.
  const seeded = [pick(upper), pick(lower), pick(digit), pick(symbol)];
  for (let i = 0; i < 12; i += 1) seeded.push(pick(all));
  // Shuffle so the required chars aren't always at the front.
  return seeded.sort(() => Math.random() - 0.5).join('');
};

const TEAM_OPTIONS = [
  { value: '', label: '— Unassigned —' },
  { value: 'advisory', label: 'Advisory' },
  { value: 'sponsors', label: 'Sponsors' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'operations', label: 'Operations' }
] as const;

interface Props {
  onClose: () => void;
}

const CreateEmployeeModal = ({ onClose }: Props) => {
  const dispatch = useAppDispatch();

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [erole, setErole] = useState<string>('');
  const [team, setTeam] = useState<string>('');
  const [office, setOffice] = useState<string>('');
  const [qualifications, setQualifications] = useState<string>('');
  const [mobileNumber, setMobileNumber] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Simple email regex — backend validates again.
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const requiredOk = firstName.trim() !== '' && lastName.trim() !== '' && emailValid;

  const handleSave = () => {
    if (!requiredOk) return;
    setSaving(true);
    dispatch(
      EmployeeActions.addEmployee(
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          erole,
          team: team === '' ? undefined : (team as Employee['team']),
          office,
          qualifications,
          mobile_number: mobileNumber,
          start_date: startDate || undefined,
          // Backend Employee inherits Devise + requires the model's `is_employee`
          // boolean to put the user on the Employees list.
          is_employee: true,
          // Devise :validatable requires a password on create; the controller
          // doesn't generate one for us.  We send a strong random temp value
          // and rely on the new employee using Forgot password on first login.
          password: generateTempPassword()
        } as unknown as Employee,
        // Legacy saga signature wants navigate — pass no-op.
        ((): void => undefined) as never,
        () => {
          setSaving(false);
          onClose();
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button
        type="button"
        className="btn primary"
        onClick={handleSave}
        disabled={saving || !requiredOk}
      >
        {saving ? 'Creating…' : 'Add employee'}
      </button>
    </>
  );

  return (
    <Modal title="Add employee" onClose={onClose} footer={footer}>
      <div className="field row-2">
        <div className="field" style={{ gap: 6 }}>
          <span className="label">First name *</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="field" style={{ gap: 6 }}>
          <span className="label">Last name *</span>
          <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <span className="label">Email *</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="alice@bsm.international"
        />
        {email !== '' && !emailValid && (
          <span
            className="muted"
            style={{ fontSize: 11.5, marginTop: 2, textTransform: 'none', letterSpacing: 0 }}
          >
            Doesn't look like a valid email address.
          </span>
        )}
      </div>

      <div className="field">
        <span className="label">Job title (erole)</span>
        <input
          type="text"
          value={erole}
          onChange={(e) => setErole(e.target.value)}
          placeholder="e.g. Associate Director"
        />
      </div>

      <div className="field row-2">
        <div className="field" style={{ gap: 6 }}>
          <span className="label">Team</span>
          <select value={team} onChange={(e) => setTeam(e.target.value)}>
            {TEAM_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gap: 6 }}>
          <span className="label">Office</span>
          <input
            type="text"
            value={office}
            onChange={(e) => setOffice(e.target.value)}
            placeholder="e.g. Cape Town"
          />
        </div>
      </div>

      <div className="field">
        <span className="label">Qualifications</span>
        <input
          type="text"
          value={qualifications}
          onChange={(e) => setQualifications(e.target.value)}
          placeholder="e.g. BCom (Hons), CA(SA)"
        />
      </div>

      <div className="field row-2">
        <div className="field" style={{ gap: 6 }}>
          <span className="label">Phone</span>
          <input
            type="text"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            placeholder="+27 …"
          />
        </div>
        <div className="field" style={{ gap: 6 }}>
          <span className="label">Start date</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
      </div>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 4, marginBottom: 0 }}>
        ID number, address, employment documents, leave balance and password setup happen on the
        legacy form at <code>/human-resources/employees/add</code>.
      </p>
    </Modal>
  );
};

export default CreateEmployeeModal;
